import {
	DateString,
	Highlight,
	HighlightedText,
	Icon,
	ListItem,
	Space,
	Typography
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { FileThumbnailKind, getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { getExtension } from "client_legacy/Documents/path";
import { execute } from "client_legacy/Documents/SharePoint/Actions/openLocationAction";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { DocumentItemViewModel, ListLocationViewModel } from "client/Api/Documents/SharePoint/common";
import { SubTitleSection } from "./SubTitleSection";
import { getAuthority } from "client_legacy/ndaw/urlHelper";
import { Thumbnail } from "./Thumbnail";
import { DocumentLocation } from "./DocumentLocation";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";

export type DocumentListItemProps = {
	hitHighlights?: readonly HighlightItem[];
	document: DocumentItemViewModel;
	isMobile: boolean;
	onDismiss?(): void;
	onRemove?: () => void;
	timezone: TimeZone;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const DocumentListItem = (props: DocumentListItemProps): ReactElement => {
	const {
		hitHighlights = EMPTY_ARRAY,
		document,
		isMobile,
		onDismiss,
		onRemove,
		timezone,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const {
		name,
		modifiedOn,
		modifiedBy,
		itemType,
		id
	} = document;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const documentHighlights = useMemo(
		(): Map<string, Highlight> => {
			return fromArray(
				[...highlights.entries()]
					.filter(([key]) => key.startsWith("document.")),
				([key]) => key.slice("document.".length),
				([_, value]) => value
			);
		},
		[highlights]
	);

	const locationModel: SharePointItemLocationViewModel = getLocationModelFromDocument(document);

	const locationClickHandler = useCallback(
		(): void => {
			execute(document, false);
			onDismiss?.();
		},
		[document, onDismiss]
	);

	const type: FileThumbnailKind = getDocumentKind(
		itemType,
		getExtension(name)
	);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	return (
		<ListItem
			trackingId={generateTrackingId("document-result-list-item", `|${id}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<Thumbnail
						trackingId={generateTrackingId("thumbnail-container", `|${type}`)}
					>
						<Icon
							value={{ kind: "svg", name: type }}
						/>
					</Thumbnail>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={name}
							highlight={documentHighlights.get("name")}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<ListItem.SubTitleIcon kind="pen"/>
					<Space />
					<SubTitleSection
						trackingId="general-info"
						text={(
							<>
								<DateString
									format="RelativeTimestamp"
									value={modifiedOn}
									timezone={timezone}
								/>
								<Space />
								{localize("_(Edited)By")}
								<Space />
								{modifiedBy}
							</>
						)}
					/>
					{locationModel.numberOfSegments > 0 && (
						<SubTitleSection
							trackingId="location"
							text={(
								<DocumentLocation
									onClick={locationClickHandler}
								>
									{formatFileLocation(locationModel)}
								</DocumentLocation>
							)}
						/>
					)}
				</ListItem.SubTitle>
			</ListItem.Content>
			{isMobile && (
				<ListItem.Action
					icon="folder-open"
					onClick={locationClickHandler}
					bubbleClick={false}
					trackingId="open-location"
				/>
			)}
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
DocumentListItem.displayName = "DocumentListItem";

export type SharePointItemLocationViewModel = {
	numberOfSegments: number;
	siteName: string | null;
	folderName: string | null;
};

export function getLocationModelFromDocument(item: {
	serverRelativeUrl: string;
	location: ListLocationViewModel;
}): SharePointItemLocationViewModel {
	const absoluteUrl: string = getAuthority(
		item.location
			.siteUrl
	)
		+ item.serverRelativeUrl;

	const segments: string[] = absoluteUrl.split("/");

	const result: SharePointItemLocationViewModel = {
		numberOfSegments: 0,
		siteName: null,
		folderName: null,
	};

	if (segments.length > 3) {
		result.numberOfSegments = segments.length - 3;
		result.siteName = segments[3];
	}

	if (segments.length > 4) {
		result.folderName = segments[segments.length - 2];
		if (result.siteName!.length > 21) {
			result.siteName = result.siteName!.substring(0, 17) + "...";
		}
	}

	return result;
}

export function formatFileLocation(
	locationModel: SharePointItemLocationViewModel
): string | null {
	let fileLocationString: string | null = locationModel.siteName;

	if (locationModel.numberOfSegments > 1) {
		fileLocationString += " » ";

		if (locationModel.numberOfSegments > 2) {
			fileLocationString += "... » ";
		}

		fileLocationString! += locationModel.folderName;
	}

	return fileLocationString;
}